import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import useHeaderTitleContext from '../../hooks/useHeaderTitleContext';
import useUserContext from '../../hooks/useUserContext';
import routes from '../../setup/consts/routes';
import {
  BuyerHeaders,
  CreateOrderBody,
  DeliverTo,
  OrderView,
  PaymentMethod,
  Product,
  SortKey,
} from './types';
import { BRANDS, CATEGORIES, PRODUCTS } from './data';
import {
  categoryLabel,
  mapPlatformBrand,
  mapPlatformListing,
  mapPlatformSupplier,
} from './liveData';
import { tierPrice, cartSubtotal, cartFreight } from './pricing';
import { MktCartProvider, useMktCart } from './MktCartContext';
import { useBrands, useCatalog, useCatalogDetail, useCreateOrders, useMyOrders, useQuote } from './hooks';
import { emitMktEvent } from './events';
import ScreenHome from './ScreenHome';
import ScreenProduct from './ScreenProduct';
import ScreenCart from './ScreenCart';
import ScreenCheckout from './ScreenCheckout';
import ScreenConfirmed from './ScreenConfirmed';
import ScreenOrders from './ScreenOrders';

// '1' = read the live marketplace PLATFORM; off → mock catalog. Both render
// the same screens.
const MARKETPLACE_LIVE = process.env.REACT_APP_ENABLE_MARKETPLACE_LIVE === '1';

type View = 'home' | 'product' | 'cart' | 'checkout' | 'confirmed' | 'orders';

const MarketplaceInner: React.FC = () => {
  const intl = useIntl();
  const history = useHistory();
  const { setHeaderTitle } = useHeaderTitleContext();
  const { user } = useUserContext();
  const cart = useMktCart();

  const [view, setView] = useState<View>('home');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirmedOrders, setConfirmedOrders] = useState<OrderView[]>([]);
  const [confirmedPaymentMethod, setConfirmedPaymentMethod] = useState<PaymentMethod>('wallet');

  // Home filters/sort/search.
  const [cat, setCat] = useState('All');
  const [brandFilter, setBrandFilter] = useState<string | null>(null);
  const [sort, setSort] = useState<SortKey>('popular');
  const [search, setSearch] = useState('');

  const buyer: BuyerHeaders = {
    buyerUserId: user?.id ?? '',
    buyerClientId: user?.clientId,
  };
  const eventsEnabled = MARKETPLACE_LIVE && !!user?.id;
  const merchantId = user?.id ?? '';

  useEffect(() => {
    setHeaderTitle(intl.formatMessage({ id: 'mainNavigation.marketplace', defaultMessage: 'Marketplace' }));
  }, [intl, setHeaderTitle]);

  // ── Data source: live catalog vs mock ──
  const {
    items: liveItems,
    isLoading: catalogLoading,
    isError: catalogError,
    refetch: refetchCatalog,
  } = useCatalog({
    enabled: MARKETPLACE_LIVE,
    q: search || undefined,
    category: cat,
    brand: brandFilter || undefined,
    sort,
    pageSize: 60,
  });
  const { brands: liveBrands } = useBrands(MARKETPLACE_LIVE);

  const products: Product[] = useMemo(
    () => (MARKETPLACE_LIVE ? liveItems.map(mapPlatformListing) : PRODUCTS),
    [liveItems]
  );
  const brands = MARKETPLACE_LIVE
    ? liveBrands.map(mapPlatformBrand)
    : BRANDS;

  // Category pills. Live API categories are slugs; accumulate every slug seen
  // (so the list stays complete when a category filter narrows the response).
  // The pill VALUE is the raw slug the server expects; the LABEL is prettified.
  const seenCatsRef = useRef<Set<string>>(new Set());
  if (MARKETPLACE_LIVE) {
    products.forEach((p) => p.cat && seenCatsRef.current.add(p.cat));
  }
  const categories = MARKETPLACE_LIVE
    ? ['All', ...Array.from(seenCatsRef.current).sort()]
    : CATEGORIES;

  // Mock-mode client-side filtering/sorting (live mode filters server-side).
  const homeProducts: Product[] = useMemo(() => {
    if (MARKETPLACE_LIVE) return products;
    let list = products.slice();
    if (cat !== 'All') list = list.filter((p) => p.cat === cat);
    if (brandFilter) list = list.filter((p) => p.brand === brandFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.brand.toLowerCase().includes(q) ||
          p.cat.toLowerCase().includes(q)
      );
    }
    if (sort === 'price_asc') list.sort((a, b) => (a.tiers[0]?.price ?? 0) - (b.tiers[0]?.price ?? 0));
    if (sort === 'price_desc') list.sort((a, b) => (b.tiers[0]?.price ?? 0) - (a.tiers[0]?.price ?? 0));
    if (sort === 'rating') list.sort((a, b) => b.rating - a.rating);
    return list;
  }, [products, cat, brandFilter, search, sort]);

  // Detail: live fetch by id (full listing + supplier), else mock lookup.
  const { listing: liveDetail } = useCatalogDetail(
    view === 'product' ? selectedId : null,
    MARKETPLACE_LIVE
  );
  const selectedProduct: Product | null = useMemo(() => {
    if (!selectedId) return null;
    if (MARKETPLACE_LIVE && liveDetail) return mapPlatformListing(liveDetail);
    return products.find((p) => p.id === selectedId) ?? null;
  }, [selectedId, liveDetail, products]);
  const selectedSupplier =
    MARKETPLACE_LIVE && liveDetail?.supplier ? mapPlatformSupplier(liveDetail.supplier) : null;

  const lines = cart.lines(products);

  // SERVER-OWNED freight for the whole cart. One hook, keyed on cart contents,
  // shared by cart + checkout (cache stays warm across the hop). Live mode only;
  // mock mode has no quote — mock confirmation freight comes from cartFreight().
  const { quote, isFetching: quoteFetching, isError: quoteError } = useQuote(
    lines,
    MARKETPLACE_LIVE
  );

  const { createOrders, isPlacing, isError: orderError, reset: resetOrder } = useCreateOrders(buyer);

  // My Orders: fetch live when the orders view is active; no-op in mock mode.
  const { orders: myOrders, isLoading: ordersLoading, isError: ordersError, refetch: refetchOrders } = useMyOrders(buyer, MARKETPLACE_LIVE && view === 'orders');

  // ── Funnel events ──
  // catalog_viewed once per home mount.
  useEffect(() => {
    if (view === 'home') {
      emitMktEvent('catalog_viewed', { cat, sort }, { merchantId, enabled: eventsEnabled });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const openProduct = (id: string) => {
    setSelectedId(id);
    setView('product');
    emitMktEvent('product_viewed', { productId: id }, { merchantId, enabled: eventsEnabled });
  };

  const addToCart = (p: Product, qty?: number) => {
    const firstItem = cart.count === 0;
    if (qty != null) cart.setQty(p, qty);
    else cart.addMoq(p);
    emitMktEvent(firstItem ? 'cart_started' : 'cart_updated', { productId: p.id }, { merchantId, enabled: eventsEnabled });
  };

  const goCart = () => setView('cart');
  const goCheckout = () => {
    setView('checkout');
    emitMktEvent('checkout_started', { items: lines.length, subtotal: cartSubtotal(lines) }, { merchantId, enabled: eventsEnabled });
  };

  // ── Place order → POST /orders → confirmation (per-merchant split). ──
  const placeOrder = async ({
    paymentMethod,
    deliverTo,
    deliverAddress,
  }: {
    paymentMethod: PaymentMethod;
    deliverTo: DeliverTo;
    deliverAddress?: string;
  }) => {
    // Freight is SERVER-OWNED — we do NOT send freightIqd (the server computes
    // it and ignores any client value). The placed-order response carries the
    // authoritative freight per PO.
    const body: CreateOrderBody = {
      items: lines.map(({ product, qty }) => ({ listingId: product.id, qty })),
      paymentMethod,
      deliverTo,
      ...(deliverAddress ? { deliverAddress } : {}),
    };

    if (MARKETPLACE_LIVE) {
      try {
        const res = await createOrders(body);
        // Server OrderView doesn't return stockUnit yet — enrich from the cart
        // lines still in scope so the receipt shows case/صندوق, not وحدة (M-09).
        const enriched = res.orders.map((o) => ({
          ...o,
          items: o.items.map((it) => ({
            ...it,
            stockUnit: it.stockUnit ?? lines.find((l) => l.product.id === it.listingId)?.product.stockUnit,
          })),
        }));
        setConfirmedOrders(enriched);
        setConfirmedPaymentMethod(paymentMethod);
        cart.clear();
        setView('confirmed');
      } catch {
        // orderError drives the inline error; never fake success.
      }
      return;
    }

    // Mock mode (no backend): synthesize the per-merchant split locally so the
    // confirmation screen is demoable. Groups lines by brand = merchant.
    const byMerchant = new Map<string, typeof lines>();
    lines.forEach((l) => {
      const key = l.product.brand || 'Supplier';
      byMerchant.set(key, [...(byMerchant.get(key) ?? []), l]);
    });
    let seq = 48000 + Math.floor(Math.random() * 900);
    const orders: OrderView[] = Array.from(byMerchant.entries()).map(([merchantName, group]) => {
      const items = group.map(({ product, qty }) => ({
        listingId: product.id,
        name: product.name,
        nameAr: product.nameAr,
        qty,
        unitPriceIqd: tierPrice(product, qty),
        lineTotalIqd: tierPrice(product, qty) * qty,
        stockUnit: product.stockUnit,
      }));
      const subtotalIqd = items.reduce((s, it) => s + it.lineTotalIqd, 0);
      const freightIqd = cartFreight(group, false) ?? 0;
      seq += 1;
      return {
        id: `mock-${seq}`,
        code: `SO-${seq}`,
        merchantName,
        items,
        subtotalIqd,
        freightIqd,
        totalIqd: subtotalIqd + freightIqd,
        stage: 'Confirmed',
      };
    });
    setConfirmedOrders(orders);
    setConfirmedPaymentMethod(paymentMethod);
    cart.clear();
    setView('confirmed');
  };

  const backToHome = () => setView('home');
  const goOrders = () => setView('orders');

  switch (view) {
    case 'product':
      return selectedProduct ? (
        <ScreenProduct
          product={selectedProduct}
          supplier={selectedSupplier}
          initialQty={cart.cart[selectedProduct.id] || 0}
          freightEnabled={MARKETPLACE_LIVE}
          onBack={backToHome}
          onAddToCart={(p, qty) => { addToCart(p, qty); goCart(); }}
          onBuyNow={(p, qty) => { addToCart(p, qty); goCheckout(); }}
        />
      ) : (
        <ScreenHome
          products={homeProducts}
          brands={brands}
          categories={categories}
          categoryLabel={categoryLabel}
          cartCount={cart.count}
          cart={cart.cart}
          loading={MARKETPLACE_LIVE && catalogLoading}
          error={MARKETPLACE_LIVE && catalogError}
          onRetry={refetchCatalog}
          onOpenProduct={openProduct}
          onAddToCart={(p) => addToCart(p)}
          onOpenCart={goCart}
          onOpenOrders={goOrders}
          search={search}
          onSearch={setSearch}
          sort={sort}
          onSort={setSort}
          cat={cat}
          onCat={setCat}
          brandFilter={brandFilter}
          onBrandFilter={setBrandFilter}
        />
      );

    case 'cart':
      return (
        <ScreenCart
          lines={lines}
          quote={quote}
          quoteLoading={quoteFetching}
          quoteError={MARKETPLACE_LIVE && quoteError}
          onBack={backToHome}
          onOpenProduct={openProduct}
          onStepUp={cart.stepUp}
          onStepDown={cart.stepDown}
          onRemove={cart.remove}
          onCheckout={goCheckout}
        />
      );

    case 'checkout':
      return (
        <ScreenCheckout
          lines={lines}
          quote={quote}
          quoteLoading={quoteFetching}
          quoteError={MARKETPLACE_LIVE && quoteError}
          placing={isPlacing}
          error={MARKETPLACE_LIVE && orderError}
          onBack={() => { resetOrder(); setView('cart'); }}
          onPlaceOrder={placeOrder}
        />
      );

    case 'confirmed':
      return (
        <ScreenConfirmed
          orders={confirmedOrders}
          paymentMethod={confirmedPaymentMethod}
          onTrackOrders={goOrders}
          onKeepSourcing={() => { setConfirmedOrders([]); setConfirmedPaymentMethod('wallet'); setView('home'); }}
        />
      );

    case 'orders':
      return (
        <ScreenOrders
          orders={MARKETPLACE_LIVE ? myOrders : confirmedOrders}
          isLoading={MARKETPLACE_LIVE && ordersLoading}
          isError={MARKETPLACE_LIVE && ordersError}
          cartCount={cart.count}
          onBack={backToHome}
          onOpenCart={goCart}
          onRetry={refetchOrders}
        />
      );

    case 'home':
    default:
      return (
        <ScreenHome
          products={homeProducts}
          brands={brands}
          categories={categories}
          categoryLabel={categoryLabel}
          cartCount={cart.count}
          cart={cart.cart}
          loading={MARKETPLACE_LIVE && catalogLoading}
          error={MARKETPLACE_LIVE && catalogError}
          onRetry={refetchCatalog}
          onOpenProduct={openProduct}
          onAddToCart={(p) => addToCart(p)}
          onOpenCart={goCart}
          onOpenOrders={goOrders}
          search={search}
          onSearch={setSearch}
          sort={sort}
          onSort={setSort}
          cat={cat}
          onCat={setCat}
          brandFilter={brandFilter}
          onBrandFilter={setBrandFilter}
        />
      );
  }
};

const MarketplacePage: React.FC = () => (
  <MktCartProvider>
    <MarketplaceInner />
  </MktCartProvider>
);

export default MarketplacePage;
